import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../components/Auth";
import { Pill, StatusBadge } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { useFeedback } from "../components/Feedback";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { api, type DocStatus, type Party, type SessionRow, type User } from "../lib/api";
import { day, money, num, titleCase, when } from "../lib/format";
import { useApi } from "../lib/useApi";
import { Field } from "./DocumentForm";

interface Company { name: string; address: string; attn: string; phone: string; email: string; fda_no: string; import_permit: string; tax_id: string }
type Numbering = Record<"sales_invoice" | "transfer_invoice" | "purchase_order", { prefix: string; next: number }>;
interface SettingsT { company: Partial<Company>; numbering: Numbering; system: { environment: string; data_dir: string; bci_offline: boolean; last_import: string | null; counts: Record<string, number> } }

export function Settings() {
  const { user } = useAuth();
  const TABS = [
    { id: "company", label: "Company" },
    { id: "numbering", label: "Numbering" },
    { id: "address-book", label: "Address book" },
    { id: "account", label: "Your account" },
    ...(user?.role === "admin" ? [{ id: "people", label: "People" }] : []),
    { id: "system", label: "System" },
  ];
  const [tab, setTab] = useTab(TABS);
  const { data, error, reload } = useApi<SettingsT>("/settings");
  return (
    <>
      <PageHeader title="Settings" />
      <Tabs tabs={TABS} current={tab} onChange={setTab} />
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!data ? <Loading /> : (
        <>
          {tab === "company" && <CompanyForm initial={data.company} />}
          {tab === "numbering" && <NumberingForm initial={data.numbering} />}
          {tab === "address-book" && <AddressBook />}
          {tab === "account" && <YourAccount />}
          {tab === "people" && user?.role === "admin" && <><People /><Activity /></>}
          {tab === "system" && (
            <Card title="System">
              <Facts items={[["Environment", data.system.environment], ["Data folder", <code>{data.system.data_dir}</code>],
                ["BCI", data.system.bci_offline ? "offline (saved copy — test mode)" : "live Google Sheet"],
                ["Last import", when(data.system.last_import)],
                ...Object.entries(data.system.counts).map(([k, v]) => [titleCase(k), num(v)] as [string, string])]} />
            </Card>
          )}
        </>
      )}
    </>
  );
}

function YourAccount() {
  const { user, signOut } = useAuth();
  const { toast } = useFeedback();
  const sessions = useApi<SessionRow[]>("/auth/sessions");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const change = async () => {
    setError(null);
    if (next !== again) { setError("The two new passwords do not match."); return; }
    try {
      const r = await api.post<{ other_sessions_ended: number }>("/auth/password",
        { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setAgain("");
      toast(r.other_sessions_ended
        ? `Password changed; ${r.other_sessions_ended} other browser(s) signed out`
        : "Password changed");
      sessions.reload();
    } catch (e) { setError((e as Error).message); }
  };

  const endOthers = async () => {
    try {
      const r = await api.post<{ ended: number }>("/auth/sessions/revoke-others");
      toast(r.ended ? `Signed out ${r.ended} other browser(s)` : "No other browsers were signed in");
      sessions.reload();
    } catch (e) { toast((e as Error).message, "error"); }
  };

  return (
    <>
      <Card title="Your account">
        <Facts items={[["Name", user?.name], ["Email", user?.email], ["Role", titleCase(user?.role)],
          ["Last signed in", when(user?.last_login_at)]]} />
        <p className="note muted">Ask an administrator to change your name, email or role.</p>
      </Card>
      <Card title="Change your password" actions={<button className="btn btn-primary" onClick={change}>Change password</button>}>
        {error && <ErrorBox error={error} />}
        <div className="form-grid">
          <Field label="Current password"><input type="password" autoComplete="current-password" aria-label="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
          <Field label="New password" hint="At least 12 characters"><input type="password" autoComplete="new-password" aria-label="New password" value={next} onChange={(e) => setNext(e.target.value)} /></Field>
          <Field label="New password again"><input type="password" autoComplete="new-password" aria-label="New password again" value={again} onChange={(e) => setAgain(e.target.value)} /></Field>
        </div>
        <p className="note muted">Changing it signs out every other browser you are signed in on.</p>
      </Card>
      <Card title="Where you are signed in" actions={<><button className="btn btn-sm" onClick={endOthers}>Sign out other browsers</button><button className="btn btn-sm" onClick={() => void signOut()}>Sign out here</button></>}>
        {sessions.data ? (
          <DataTable<SessionRow> rows={sessions.data} rowKey={(r) => r.id} filterPlaceholder={false} testId="sessions-table"
            columns={[
              { key: "current", label: "", render: (r) => (r.current ? <Pill tone="good">this browser</Pill> : "") },
              { key: "last_seen_at", label: "Last used", render: (r) => when(r.last_seen_at) },
              { key: "created_at", label: "Signed in", render: (r) => when(r.created_at) },
              { key: "ip", label: "From", render: (r) => r.ip ?? "—" },
              { key: "user_agent", label: "Browser", render: (r) => (r.user_agent ?? "—").slice(0, 60) },
            ]} />
        ) : <Loading />}
      </Card>
    </>
  );
}

function People() {
  const { user: me } = useAuth();
  const { data, error, reload } = useApi<User[]>("/auth/users");
  const { toast, confirm } = useFeedback();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [formError, setFormError] = useState<string | null>(null);

  const add = async () => {
    setFormError(null);
    try {
      await api.post("/auth/users", form);
      toast(`${form.name} can now sign in`);
      setAdding(false);
      setForm({ name: "", email: "", password: "", role: "user" });
      reload();
    } catch (e) { setFormError((e as Error).message); }
  };

  const setActive = async (u: User, active: boolean) => {
    if (!active && !(await confirm({ title: `Turn off access for ${u.name}?`,
      body: "They are signed out everywhere immediately. Their history stays on the documents they touched.",
      confirm: "Turn off access", danger: true }))) return;
    try {
      await api.put(`/auth/users/${u.id}`, { name: u.name, role: u.role, active });
      toast(active ? `${u.name} can sign in again` : `${u.name} can no longer sign in`);
      reload();
    } catch (e) { toast((e as Error).message, "error"); }
  };

  const resetPassword = async (u: User) => {
    const temp = `${Math.random().toString(36).slice(2, 8)}-${Math.random().toString(36).slice(2, 8)}-${Math.random().toString(36).slice(2, 8)}`;
    if (!(await confirm({ title: `Reset the password for ${u.name}?`,
      body: <>They will be signed out everywhere and must set a new password at their next sign-in. Give them this temporary password: <code>{temp}</code></>,
      confirm: "Reset password", danger: true }))) return;
    try {
      await api.post(`/auth/users/${u.id}/reset-password`, { password: temp });
      toast(`Temporary password for ${u.name}: ${temp}`);
      reload();
    } catch (e) { toast((e as Error).message, "error"); }
  };

  return (
    <Card title="People with access" actions={<button className="btn btn-primary" onClick={() => setAdding((a) => !a)}>{adding ? "Cancel" : "Add person"}</button>}>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {adding && (
        <div className="add-user">
          {formError && <ErrorBox error={formError} />}
          <div className="form-grid">
            <Field label="Name"><input aria-label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><input type="email" aria-label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Temporary password" hint="At least 12 characters. They must change it at first sign-in.">
              <input aria-label="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
            <Field label="Role" hint="Administrators can also manage people">
              <select aria-label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="user">User</option><option value="admin">Administrator</option>
              </select>
            </Field>
          </div>
          <button className="btn btn-primary" onClick={add}>Create account</button>
        </div>
      )}
      {!data ? <Loading /> : (
        <DataTable<User> rows={data} rowKey={(u) => u.id} filterPlaceholder={false} testId="users-table" columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "role", label: "Role", render: (u) => <Pill tone={u.role === "admin" ? "info" : "neutral"}>{titleCase(u.role)}</Pill> },
          { key: "active", label: "Access", render: (u) => (u.active ? <Pill tone="good">active</Pill> : <Pill tone="muted">off</Pill>) },
          { key: "last_login_at", label: "Last signed in", render: (u) => (u.last_login_at ? when(u.last_login_at) : <span className="muted">never</span>) },
          { key: "last_active_at", label: "Last active", render: (u) => (u.last_active_at ? when(u.last_active_at) : <span className="muted">—</span>) },
          { key: "actions", label: "", render: (u) => (u.id === me?.id ? <span className="muted">you</span> : (
            <span className="row-actions">
              <button className="btn btn-sm" onClick={() => resetPassword(u)}>Reset password</button>
              <button className="btn btn-sm" onClick={() => setActive(u, !u.active)}>{u.active ? "Turn off" : "Turn on"}</button>
            </span>)) },
        ]} />
      )}
    </Card>
  );
}

interface ActivityRow { id: number; at: string; user_id: number | null; name: string | null; email: string | null; action: string; summary: string; ip: string | null }

const ACTION_TONE: Record<string, string> = { sign_in_failed: "warning", signed_in: "good", people: "info", changed: "info", uploaded: "info", synced: "info" };

/** What people did: sign-ins, pages they looked at, files and documents they changed. */
function Activity() {
  const people = useApi<User[]>("/auth/users");
  const [person, setPerson] = useState("");
  const { data, error, reload } = useApi<ActivityRow[]>(`/auth/activity?limit=300${person ? `&user_id=${person}` : ""}`);
  return (
    <Card title="Activity" actions={
      <select aria-label="Whose activity" value={person} onChange={(e) => setPerson(e.target.value)}>
        <option value="">Everyone</option>
        {(people.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>}>
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!data ? <Loading /> : (
        <DataTable<ActivityRow> rows={data} rowKey={(r) => r.id} testId="activity-table" filterPlaceholder="Filter activity…"
          empty="Nothing recorded yet." columns={[
            { key: "at", label: "When", render: (r) => when(r.at) },
            { key: "name", label: "Who", value: (r) => r.name ?? r.email ?? "", render: (r) => r.name ?? <span className="muted">{r.email ?? "—"}</span> },
            { key: "summary", label: "What", render: (r) => <>{r.summary}{ACTION_TONE[r.action] === "warning" ? <> <Pill tone="warning">failed</Pill></> : null}</> },
            { key: "ip", label: "From", render: (r) => (r.ip ? <code>{r.ip}</code> : "—") },
          ]} />
      )}
      <p className="note muted">
        Sign-ins, the pages each person opened (once per page every 15 minutes), downloads, uploads and every change
        they made. The newest 300 are shown.
      </p>
    </Card>
  );
}

function CompanyForm({ initial }: { initial: Partial<Company> }) {
  const [c, setC] = useState<Company>({ name: "", address: "", attn: "", phone: "", email: "", fda_no: "", import_permit: "", tax_id: "", ...initial });
  const [saving, setSaving] = useState(false);
  const { toast } = useFeedback();
  const save = async () => {
    setSaving(true);
    try { setC(await api.put<Company>("/settings/company", c)); toast("Company details saved"); }
    catch (e) { toast((e as Error).message, "error"); } finally { setSaving(false); }
  };
  const f = (k: keyof Company, label: string, hint?: string) => (
    <Field label={label} hint={hint}><input aria-label={label} value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} /></Field>
  );
  return (
    <Card title="Company details" actions={<button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>}>
      <p className="note muted">Used to fill in new documents. Documents already issued keep the details they were issued with.</p>
      <div className="form-grid">
        {f("name", "Company name")}{f("attn", "Contact (ATTN)")}{f("phone", "Phone")}{f("email", "Email")}
        <Field label="Address"><textarea aria-label="Address" rows={3} value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })} /></Field>
        {f("fda_no", "FDA #", "Printed on sales invoices")}{f("import_permit", "Import permit #", "Printed on transfer invoices")}{f("tax_id", "Tax ID")}
      </div>
    </Card>
  );
}

function NumberingForm({ initial }: { initial: Numbering }) {
  const [n, setN] = useState<Numbering>(initial);
  const { toast } = useFeedback();
  const save = async () => {
    try { setN(await api.put<Numbering>("/settings/numbering", n)); toast("Numbering saved"); }
    catch (e) { toast((e as Error).message, "error"); }
  };
  const rows: [keyof Numbering, string][] = [["sales_invoice", "Sales invoices"], ["transfer_invoice", "Transfer invoices"], ["purchase_order", "Purchase orders"]];
  return (
    <Card title="Document numbering" actions={<button className="btn btn-primary" onClick={save}>Save</button>}>
      <p className="note muted">The next number is suggested on every new document and moves forward on issue. A number already used is always skipped.</p>
      <table className="grid">
        <thead><tr><th>Document</th><th>Prefix</th><th>Next number</th><th>Next will be</th></tr></thead>
        <tbody>
          {rows.map(([k, label]) => (
            <tr key={k}>
              <td>{label}</td>
              <td><input aria-label={`${label} prefix`} value={n[k]?.prefix ?? ""} onChange={(e) => setN({ ...n, [k]: { ...n[k], prefix: e.target.value } })} /></td>
              <td><input aria-label={`${label} next number`} type="number" min={1} value={n[k]?.next ?? 1} onChange={(e) => setN({ ...n, [k]: { ...n[k], next: Number(e.target.value) } })} /></td>
              <td><code>{n[k]?.prefix}{n[k]?.next}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function AddressBook() {
  const { data, error, reload } = useApi<Party[]>("/parties");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  return (
    <Card actions={<Link className="btn btn-primary" to="/settings/address-book/new">Add contact</Link>}>
      {!data ? <Loading /> : (
        <DataTable<Party> testId="parties-table" rows={data} rowKey={(p) => p.id} href={(p) => `/settings/address-book/${p.id}`} columns={[
          { key: "name", label: "Name", render: (p) => <Link to={`/settings/address-book/${p.id}`}>{p.name}</Link> },
          { key: "kind", label: "Kind", render: (p) => <Pill>{titleCase(p.kind)}</Pill> },
          { key: "attn", label: "Contact", render: (p) => p.attn ?? "—" },
          { key: "phone", label: "Phone", render: (p) => p.phone ?? "—" },
          { key: "address", label: "Address", render: (p) => (p.address ? p.address.split("\n")[0] : "—") },
        ]} />
      )}
    </Card>
  );
}

const KINDS = ["own_company", "customer", "vendor", "warehouse", "broker", "carrier"];
const EMPTY: Omit<Party, "id" | "customer"> = { code: "", name: "", kind: "customer", address: "", attn: "", phone: "", email: "", reference_ids: {}, notes: "", customer_id: null, location: null, active: true };

export function PartyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useFeedback();
  const existing = useApi<Party & { documents: { id: number; doc_type: string; number: string; status: DocStatus; date: string | null; total: number }[] }>(id ? `/parties/${id}` : null);
  const locations = useApi<{ code: string; name: string }[]>("/locations");
  const customers = useApi<{ id: number; name: string; channel_name: string }[]>("/customers");
  const [p, setP] = useState(EMPTY);
  const [refs, setRefs] = useState<{ k: string; v: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing.data) {
      setP({ ...EMPTY, ...existing.data });
      setRefs(Object.entries(existing.data.reference_ids ?? {}).map(([k, v]) => ({ k, v })));
    }
  }, [existing.data]);

  if (id && !existing.data) return existing.error ? <ErrorBox error={existing.error} /> : <Loading />;

  const save = async () => {
    setError(null);
    const body = { ...p, code: p.code || p.name.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 48),
      reference_ids: Object.fromEntries(refs.filter((r) => r.k.trim()).map((r) => [r.k.trim(), r.v])) };
    try {
      const saved = id ? await api.put<Party>(`/parties/${id}`, body) : await api.post<Party>("/parties", body);
      toast(`${saved.name} saved`);
      nav(`/settings/address-book/${saved.id}`);
    } catch (e) { setError((e as Error).message); }
  };
  const f = (k: "name" | "attn" | "phone" | "email" | "code", label: string, hint?: string) => (
    <Field label={label} hint={hint}><input aria-label={label} value={(p[k] as string) ?? ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} /></Field>
  );

  return (
    <>
      <PageHeader crumbs={[{ label: "Settings", to: "/settings" }, { label: "Address book", to: "/settings?tab=address-book" }]}
        title={id ? p.name || "Contact" : "New contact"}
        actions={<><Link className="btn" to="/settings?tab=address-book">Back</Link><button className="btn btn-primary" onClick={save}>Save</button></>} />
      {error && <ErrorBox error={error} />}
      <div className="two-up">
        <Card title="Details">
          <div className="form-grid">
            {f("name", "Name (as printed)")}
            <Field label="Kind">
              <select aria-label="Kind" value={p.kind} onChange={(e) => setP({ ...p, kind: e.target.value })}>
                {KINDS.map((k) => <option key={k} value={k}>{titleCase(k)}</option>)}
              </select>
            </Field>
            {f("attn", "Contact (ATTN)")}{f("phone", "Phone")}{f("email", "Email")}
            {f("code", "Code", "Short unique key, e.g. STARBOARD. Filled from the name if empty.")}
            <Field label="Address (as printed)"><textarea aria-label="Address (as printed)" rows={3} value={p.address ?? ""} onChange={(e) => setP({ ...p, address: e.target.value })} /></Field>
            <Field label="Notes"><textarea aria-label="Notes" rows={3} value={p.notes ?? ""} onChange={(e) => setP({ ...p, notes: e.target.value })} /></Field>
          </div>
        </Card>
        <Card title="Links and references">
          <div className="form-grid one">
            <Field label="Stock location" hint="For warehouses: which location's figures this is">
              <select aria-label="Stock location" value={p.location ?? ""} onChange={(e) => setP({ ...p, location: e.target.value || null })}>
                <option value="">—</option>{(locations.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Sales customer" hint="For customers: links documents to their sales history">
              <select aria-label="Sales customer" value={p.customer_id ?? ""} onChange={(e) => setP({ ...p, customer_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>{(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.channel_name})</option>)}
              </select>
            </Field>
          </div>
          <p className="field-label">Reference numbers <span className="muted">(our vendor ID with them, account numbers…)</span></p>
          {refs.map((r, i) => (
            <div key={i} className="kv-row">
              <input aria-label={`Reference ${i + 1} name`} placeholder="e.g. Vendor ID" value={r.k} onChange={(e) => setRefs(refs.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))} />
              <input aria-label={`Reference ${i + 1} value`} placeholder="Value" value={r.v} onChange={(e) => setRefs(refs.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))} />
              <button className="icon-btn" aria-label={`Remove reference ${i + 1}`} onClick={() => setRefs(refs.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={() => setRefs([...refs, { k: "", v: "" }])}>+ Add reference</button>
        </Card>
      </div>
      {existing.data && (
        <Card title="Documents">
          <DataTable rows={existing.data.documents} rowKey={(d) => d.id} href={(d) => `/documents/${d.id}`} filterPlaceholder={false}
            empty="Not on any document yet." columns={[
              { key: "number", label: "Number", render: (d) => <Link to={`/documents/${d.id}`}>{d.number}</Link> },
              { key: "doc_type", label: "Type", render: (d) => titleCase(d.doc_type) },
              { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status} docType={d.doc_type} /> },
              { key: "date", label: "Date", render: (d) => day(d.date) }, { key: "total", label: "Total", numeric: true, render: (d) => money(d.total) },
            ]} />
        </Card>
      )}
    </>
  );
}
